import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { queueEntry, uuid } from "@/lib/offline";
import {
  ArrowLeft, Plus, Minus, Package as PackageIcon, ClipboardText, ArrowsLeftRight, X,
  Check, WifiSlash, Warning, MapPin,
} from "@phosphor-icons/react";

const UNITS = [
  { k: "unit", label: "Units", factor: 1 },
  { k: "basket", label: "Baskets", factor: 12 },
  { k: "box", label: "Box", factor: 20 },
  { k: "half_rack", label: "Half Rack", factor: 144 },
  { k: "full_rack", label: "Full Rack", factor: 288 },
];

const ACTIONS = [
  { k: "add", label: "Add to Inventory", icon: Plus, color: "bg-green-600 hover:bg-green-700 text-white", desc: "Produced, received, or returned", reasons: ["Produced", "Received", "Returned", "Transfer received", "Physical count adjustment", "Correction", "Other"] },
  { k: "take_out", label: "Take Out", icon: Minus, color: "bg-blue-600 hover:bg-blue-700 text-white", desc: "General withdrawal", reasons: ["Distribution", "Production use", "Transfer", "Damaged", "Expired", "Waste", "Count correction", "Other"] },
  { k: "packs", label: "Take Out for Packs", icon: PackageIcon, color: "bg-purple-600 hover:bg-purple-700 text-white", desc: "Reported separately", reasons: ["Packs"] },
  { k: "physical_count", label: "Physical Count", icon: ClipboardText, color: "bg-yellow-500 hover:bg-yellow-600 text-black", desc: "Proposes an adjustment (requires review)", reasons: ["Physical count"] },
  { k: "transfer", label: "Transfer Location", icon: ArrowsLeftRight, color: "bg-cyan-600 hover:bg-cyan-700 text-white", desc: "Move between locations", reasons: ["Transfer"] },
];

export default function ScanAction() {
  const { mealId } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [meal, setMeal] = useState(null);
  // iter19: auto-select Stock In (add) / Stock Out (take_out) from ?action= param.
  const initialK = sp.get("action");
  const initialAction = ACTIONS.find((a) => a.k === initialK) || null;
  const [selectedAction, setSelectedAction] = useState(initialAction);
  const [unit, setUnit] = useState("unit");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [destination, setDestination] = useState("");
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [negativeOverride, setNegativeOverride] = useState(false);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    api.get(`/meals/${mealId}`).then((r) => setMeal(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [mealId]);

  if (!meal) return <div className="p-6 text-sm text-muted-foreground font-mono">Loading meal record…</div>;

  const conv = meal.conversions || { unit: 1, basket: 12, box: 20, half_rack: 144, full_rack: 288 };
  const factor = conv[unit] || 1;
  const converted = Number(qty || 0) * factor;
  const currentInv = Number(meal.current_meals || 0);
  const wouldBeNegative = selectedAction && ["take_out", "packs", "transfer"].includes(selectedAction.k) && currentInv - converted < 0;
  const belowPar = currentInv < Number(meal.par_level || 0);
  const isAdminOrOwner = user && (user.role === "admin" || user.role === "system_owner");

  const previewText = () => {
    if (!selectedAction) return "";
    if (selectedAction.k === "add") return `Current ${currentInv} + ${converted} = ${currentInv + converted}`;
    if (selectedAction.k === "physical_count") return `System ${currentInv} vs Counted ${converted} → variance ${converted - currentInv}`;
    return `Current ${currentInv} − ${converted} = ${currentInv - converted}`;
  };

  const submit = async () => {
    if (!selectedAction) return;
    if (Number(qty) <= 0) { toast.error("Enter a quantity greater than zero"); return; }
    if (selectedAction.k === "transfer" && !destination) { toast.error("Select destination location"); return; }
    if (wouldBeNegative && !negativeOverride) { toast.error("Would go below zero — check override"); return; }
    setSaving(true);
    const payload = {
      client_uuid: uuid(),
      meal_record_id: meal.meal_record_id,
      action: selectedAction.k,
      quantity: Number(qty),
      unit,
      reason,
      notes,
      location: meal.location || "",
      destination_location: selectedAction.k === "transfer" ? destination : null,
      negative_override: negativeOverride,
      source: "qr_scan",
    };
    try {
      if (!online) {
        await queueEntry("meal_action", payload);
        toast.success("Saved offline — will sync on reconnect");
      } else {
        await api.post("/meals/action", payload);
        toast.success(`${selectedAction.label} saved`);
      }
      nav("/dashboard");
    } catch (e) {
      if (!online) {
        await queueEntry("meal_action", payload);
        toast.warning("Saved offline (retry on reconnect)");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      <Link to="/scan" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-3">
        <ArrowLeft size={14} /> Back to scan
      </Link>
      {/* Item review card */}
      <div className="card-flat border-border p-4 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="label-tech mb-1">Meal Record</div>
            <h1 data-testid="meal-title" className="font-display font-black text-2xl md:text-3xl tracking-tight">
              {meal.diet_type} — {meal.diet_code}
            </h1>
            <div className="text-xs font-mono text-muted-foreground mt-2 space-y-0.5">
              <div>ID · {meal.meal_record_id}</div>
              <div>Cycle Week {meal.cycle_week} · Position {meal.cycle_position}</div>
              {meal.location && <div className="flex items-center gap-1"><MapPin size={10} />{meal.location}</div>}
            </div>
          </div>
          <div className="shrink-0 border border-border p-2 bg-white">
            <img alt="QR" src={`${BACKEND_URL}/api/qr/png/${encodeURIComponent(meal.qr_value)}?size=140`} className="w-20 h-20 md:w-24 md:h-24" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border">
          <div>
            <div className="label-tech mb-1">On Hand</div>
            <div className="font-display font-black text-2xl">{currentInv}</div>
            <div className="text-[10px] font-mono text-muted-foreground">meals</div>
          </div>
          <div>
            <div className="label-tech mb-1">PAR</div>
            <div className="font-display font-black text-2xl">{meal.par_level}</div>
            {belowPar && <div className="text-[10px] font-bold text-destructive">BELOW PAR</div>}
          </div>
          <div>
            <div className="label-tech mb-1">Target</div>
            <div className="font-display font-black text-2xl">{meal.target_stock}</div>
          </div>
        </div>
      </div>

      {/* Action menu */}
      {!selectedAction ? (
        <div className="card-flat border-border p-4">
          <div className="label-tech mb-3">Select action</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ACTIONS.map((a) => (
              <button
                key={a.k}
                data-testid={`action-${a.k}`}
                onClick={() => { setSelectedAction(a); setReason(a.reasons[0] || ""); }}
                className={`btn-hard flex items-center gap-3 p-4 rounded-sm text-left ${a.color}`}
              >
                <a.icon size={22} weight="bold" />
                <div>
                  <div className="font-bold text-sm">{a.label}</div>
                  <div className="text-[11px] opacity-80">{a.desc}</div>
                </div>
              </button>
            ))}
            <button
              onClick={() => nav("/scan")}
              className="btn-hard flex items-center gap-3 p-4 rounded-sm text-left border border-border hover:bg-muted"
            >
              <X size={22} weight="bold" />
              <div>
                <div className="font-bold text-sm">Cancel</div>
                <div className="text-[11px] opacity-70">Return to scanner</div>
              </div>
            </button>
          </div>
        </div>
      ) : (
        <div className="card-flat border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-tech">{selectedAction.label}</div>
              <div className="font-display font-bold text-lg">{selectedAction.desc}</div>
            </div>
            <button onClick={() => setSelectedAction(null)} className="text-xs text-muted-foreground hover:text-primary">Change action</button>
          </div>

          {/* Unit selector */}
          <div className="grid grid-cols-5 gap-1 mb-3">
            {UNITS.map((u) => (
              <button
                key={u.k}
                data-testid={`unit-${u.k}`}
                onClick={() => setUnit(u.k)}
                className={`h-10 border rounded-sm text-[10px] font-bold uppercase tracking-widest btn-hard ${
                  unit === u.k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"
                }`}
              >{u.label}</button>
            ))}
          </div>

          <input
            data-testid="qty-input"
            type="number" min="0" step="0.01"
            value={qty} onChange={(e) => setQty(e.target.value)}
            placeholder={`Quantity in ${unit}`}
            className="w-full h-14 px-4 border border-border bg-background rounded-sm text-2xl font-display font-black outline-none focus:ring-2 focus:ring-primary mb-3"
          />

          {selectedAction.k === "transfer" && (
            <select
              data-testid="dest-select"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="w-full h-10 px-2 border border-border bg-background rounded-sm text-sm mb-3"
            >
              <option value="">Destination location…</option>
              {locations.map((l) => (<option key={l.location_id} value={l.location_id}>{l.name}</option>))}
            </select>
          )}

          <div className="grid grid-cols-2 gap-3 mb-3">
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="h-10 px-2 border border-border bg-background rounded-sm text-sm">
              {selectedAction.reasons.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
            <div className="text-right">
              <div className="label-tech">Converted</div>
              <div className="font-display font-black text-lg text-primary">{converted.toFixed(0)} <span className="text-xs text-muted-foreground">meals</span></div>
            </div>
          </div>

          <div className="border border-border rounded-sm p-3 bg-muted/30 font-mono text-xs mb-3">
            {previewText()}
          </div>

          <textarea
            rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="w-full px-3 py-2 border border-border bg-background rounded-sm text-sm outline-none focus:ring-2 focus:ring-primary mb-3 font-mono"
          />

          {wouldBeNegative && isAdminOrOwner && (
            <label className="flex items-center gap-2 text-xs border border-yellow-500/60 bg-yellow-500/10 rounded-sm p-2 mb-3">
              <input type="checkbox" checked={negativeOverride} onChange={(e) => setNegativeOverride(e.target.checked)} />
              <span><Warning size={12} className="inline" /> Override: allow inventory to go below zero</span>
            </label>
          )}

          <button
            data-testid="confirm-action"
            onClick={submit}
            disabled={saving || !qty}
            className="btn-hard w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-orange-600 hover:text-white disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {!online && <WifiSlash size={16} weight="bold" />}
            <Check size={16} weight="bold" />
            {saving ? "Saving…" : online ? "Confirm & Save" : "Save Offline"}
          </button>
        </div>
      )}
    </div>
  );
}
